import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PHASE3_API = `${API_BASE}/api/phase3`;

/**
 * Phase3InterviewDetail
 * 
 * Full-page view for a single interview.
 * Shows: config, candidate info, scores, transcript, reports.
 * Route: /phase3/interview/:id
 * 
 * Tabs: Overview | Scores | Transcript | HM Report | Recruiter Report
 */
function Phase3InterviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [reports, setReports] = useState([]);
  const [transcript, setTranscript] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // Email report modal

  const token = localStorage.getItem('token');
  // Derive role from stored user object
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  // HM is identified by designation (same logic as UserDashboard.js)
  const isHiringManager = storedUser.designation === 'Hiring Manager';

  // Recording state (HM only)
  const [videoUrl, setVideoUrl] = useState(null);
  const [mergedUrl, setMergedUrl] = useState(null);   // Deploy F: video + interview audio in one file
  const [mergePending, setMergePending] = useState(false);   // bundle K: both files exist, merge running
  const [videoOffsetMs, setVideoOffsetMs] = useState(0);     // bundle K: fallback player sync
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [recordingError, setRecordingError] = useState('');
  const [videoMissingReason, setVideoMissingReason] = useState('');   // e.g. candidate's upload failed

  // HM Decision state (HM only)
  const [hmDecision, setHmDecision] = useState('');
  const [hmComment, setHmComment] = useState('');
  const [hmDecisionSaving, setHmDecisionSaving] = useState(false);
  const [hmDecisionMessage, setHmDecisionMessage] = useState({ type: '', text: '' });

  // Wave 2 — HM disagreement signal (all optional)
  const [hmAgreesWithAi, setHmAgreesWithAi] = useState(null);  // null | true | false
  const [hmDisagreementType, setHmDisagreementType] = useState('');
  const [hmWhatShouldHaveHappened, setHmWhatShouldHaveHappened] = useState('');
  const [linkedAppId, setLinkedAppId] = useState(null);     // 5b: linked to a Quali5d application → decide there

  useEffect(() => {
    fetchInterview();
    fetchReports();
    fetch(`${API_BASE}/api/phase3-interviews/${id}/application`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null)).then((r) => setLinkedAppId((r && r.application_id) || null)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-fetch transcript when tab switches to it
  // Auto-fetch recordings when HM opens the HM Report tab
  useEffect(() => {
    if (activeTab === 'transcript' && !transcript) fetchTranscript();
    if (activeTab === 'hm-review' && isHiringManager && !videoUrl && !audioUrl && !recordingLoading) {
      fetchRecordings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchInterview = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${PHASE3_API}/interview/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        const data = result.data || null;
        setInterview(data);
        // Seed HM decision form with existing values if already set
        if (data?.hm_decision) setHmDecision(data.hm_decision);
        if (data?.hm_comment)  setHmComment(data.hm_comment);
        // Wave 2: seed disagreement signal
        if (data?.hm_agrees_with_ai !== undefined && data?.hm_agrees_with_ai !== null) {
          setHmAgreesWithAi(data.hm_agrees_with_ai);
        }
        if (data?.hm_disagreement_type) setHmDisagreementType(data.hm_disagreement_type);
        if (data?.hm_what_should_have_happened) setHmWhatShouldHaveHappened(data.hm_what_should_have_happened);
      } else if (response.status === 401) {
        localStorage.clear();
        navigate('/');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await fetch(`${PHASE3_API}/interview/${id}/reports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setReports(result.data?.reports || []);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const fetchTranscript = async () => {
    try {
      const response = await fetch(`${PHASE3_API}/interview/${id}/transcript`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setTranscript(result.data || { has_transcript: false, turns: [] });
      }
    } catch (error) {
      console.error('Error fetching transcript:', error);
      setTranscript({ has_transcript: false, turns: [] });
    }
  };

  const fetchRecordings = async () => {
    setRecordingLoading(true);
    setRecordingError('');
    setVideoMissingReason('');
    try {
      // Fetch video and audio SAS URLs in parallel
      const [videoRes, audioRes, mergedRes] = await Promise.allSettled([
        fetch(`${PHASE3_API}/interview/${id}/recording/video`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${PHASE3_API}/interview/${id}/recording/audio`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${PHASE3_API}/interview/${id}/recording/merged`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (mergedRes.status === 'fulfilled' && mergedRes.value.ok) {
        const r = await mergedRes.value.json();
        setMergedUrl(r.data?.url || null);
        setMergePending(false);
      } else if (mergedRes.status === 'fulfilled' && mergedRes.value.status === 404) {
        const r = await mergedRes.value.json().catch(() => ({}));
        setMergePending(!!(r.detail && typeof r.detail === 'object' && r.detail.pending));
      }

      if (videoRes.status === 'fulfilled' && videoRes.value.ok) {
        const r = await videoRes.value.json();
        setVideoUrl(r.data?.url || null);
        setVideoOffsetMs(Number(r.data?.video_offset_ms) || 0);
      } else if (videoRes.status === 'fulfilled' && videoRes.value.status === 404) {
        // The backend explains a missing video when the candidate's browser reported a failed upload
        const r = await videoRes.value.json().catch(() => ({}));
        if (r.detail && typeof r.detail === 'object' && r.detail.message) setVideoMissingReason(r.detail.message);
      }
      if (audioRes.status === 'fulfilled' && audioRes.value.ok) {
        const r = await audioRes.value.json();
        setAudioUrl(r.data?.url || null);
      }
      // If neither resolved, show a message
      if (
        (videoRes.status !== 'fulfilled' || !videoRes.value.ok) &&
        (audioRes.status !== 'fulfilled' || !audioRes.value.ok)
      ) {
        setRecordingError('No recordings available for this interview.');
      }
    } catch (e) {
      console.error('Error fetching recordings:', e);
      setRecordingError('Failed to load recordings. Please try again.');
    } finally {
      setRecordingLoading(false);
    }
  };

  // Bundle K: the merge finishes ~1 min after the call (interview 61's page asked 5 s too early and never
  // again) — while it is pending, ask again every MERGE_POLL_MS for up to MERGE_POLL_TRIES.
  useEffect(() => {
    if (!mergePending || mergedUrl) return undefined;
    let tries = 0;
    let stopped = false;
    let timer = null;
    const poll = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const res = await fetch(`${PHASE3_API}/interview/${id}/recording/merged`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const r = await res.json();
          if (!stopped && r.data?.url) { setMergedUrl(r.data.url); setMergePending(false); return; }
        }
      } catch (e) { /* try again */ }
      if (!stopped && tries < MERGE_POLL_TRIES) timer = setTimeout(poll, MERGE_POLL_MS);
      else if (!stopped) setMergePending(false);
    };
    timer = setTimeout(poll, MERGE_POLL_MS);
    return () => { stopped = true; if (timer) clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergePending, mergedUrl]);

  const handleSaveHmDecision = async () => {
    if (!hmDecision) {
      setHmDecisionMessage({ type: 'error', text: 'Please select a decision before saving.' });
      return;
    }
    setHmDecisionSaving(true);
    setHmDecisionMessage({ type: '', text: '' });
    try {
      // Wave 2: build the body carefully — only include disagreement fields
      // when the HM has actually answered the agree/disagree question.
      const payload = {
        hm_decision: hmDecision,
        hm_comment: hmComment || null,
      };
      if (hmAgreesWithAi !== null) {
        payload.hm_agrees_with_ai = hmAgreesWithAi;
      }
      // Only send disagreement details when HM explicitly disagrees
      if (hmAgreesWithAi === false) {
        payload.hm_disagreement_type = hmDisagreementType || null;
        payload.hm_what_should_have_happened = hmWhatShouldHaveHappened || null;
      }

      const res = await fetch(`${PHASE3_API}/interview/${id}/hm-decision`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (res.ok) {
        setHmDecisionMessage({ type: 'success', text: 'Decision saved successfully.' });
        // Update local interview state so the saved timestamp displays immediately
        setInterview(prev => ({
          ...prev,
          hm_decision: result.data.hm_decision,
          hm_comment: result.data.hm_comment,
          hm_decision_updated_at: result.data.hm_decision_updated_at,
          hm_agrees_with_ai: result.data.hm_agrees_with_ai,
          hm_disagreement_type: result.data.hm_disagreement_type,
          hm_what_should_have_happened: result.data.hm_what_should_have_happened,
          hm_agrees_with_ai_at: result.data.hm_agrees_with_ai_at,
        }));
      } else {
        setHmDecisionMessage({ type: 'error', text: result.detail || 'Failed to save decision.' });
      }
    } catch (e) {
      setHmDecisionMessage({ type: 'error', text: 'Connection error. Please try again.' });
    } finally {
      setHmDecisionSaving(false);
    }
  };

  // Actions
  const handleAction = async (action, label) => {
    setActionLoading(action);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${PHASE3_API}/interview/${id}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: result.message || `${label} successful!` });
        fetchInterview();
      } else {
        setMessage({ type: 'error', text: result.detail || `${label} failed` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Connection error' });
    } finally {
      setActionLoading('');
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    }
  };

  // Status badge
  const getStatusBadge = (status) => {
    const config = {
      scheduled: { bg: 'bg-blue-100 text-blue-800 border-blue-200', icon: '📅', label: 'Scheduled' },
      ready: { bg: 'bg-cyan-100 text-cyan-800 border-cyan-200', icon: '✅', label: 'Ready' },
      in_progress: { bg: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '🎙️', label: 'In Progress' },
      completed: { bg: 'bg-orange-100 text-orange-800 border-orange-200', icon: '⏳', label: 'Completed' },
      processed: { bg: 'bg-green-100 text-green-800 border-green-200', icon: '🏆', label: 'Scored' },
      cancelled: { bg: 'bg-red-100 text-red-800 border-red-200', icon: '❌', label: 'Cancelled' }
    };
    const s = status?.toLowerCase() || 'scheduled';
    const c = config[s] || config.scheduled;
    return (
      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold border ${c.bg}`}>
        {c.icon} {c.label}
      </span>
    );
  };

  const getRecBadge = (rec, reason) => {
    if (!rec) return null;
    const config = {
      EXCEEDS_BAR: { bg: 'bg-green-500', icon: '🌟' },
      MEETS_BAR: { bg: 'bg-blue-500', icon: '✅' },
      BORDERLINE: { bg: 'bg-yellow-500', icon: '⚠️' },
      BELOW_BAR: { bg: 'bg-red-500', icon: '❌' }
    };
    const c = config[rec] || {};
    return (
      <span className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl text-white font-bold text-sm ${c.bg}`}
            data-testid="verdict-badge">
        {c.icon} {verdictLabel(rec, reason)}
      </span>
    );
  };

  // Safe skills parser
  const parseSkills = (skillsValue) => {
    if (!skillsValue) return '—';
    try {
      const parsed = typeof skillsValue === 'string' ? JSON.parse(skillsValue) : skillsValue;
      return Array.isArray(parsed) ? parsed.join(', ') : String(skillsValue);
    } catch {
      return String(skillsValue);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mx-auto"></div>
          <p className="text-gray-600 mt-6 font-medium">Loading interview details...</p>
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Interview Not Found</h2>
          <button onClick={() => navigate(-1)} className="mt-4 px-6 py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-all">
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const status = interview.status?.toLowerCase();
  const hasScore = !!interview.overall_score;
  const hmReport = reports.find(r => r.report_type === 'HIRING_MANAGER');
  const recruiterReport = reports.find(r => r.report_type === 'RECRUITER');

  const allTabs = [
    { key: 'overview',          label: '📋 Overview' },
    { key: 'hm-review',         label: '👔 HM Review',  hmOnly: true },
    { key: 'recruiter-report',  label: '🔍 Recruiter',  nonHmOnly: true },
    { key: 'transcript',        label: '💬 Transcript' },
    { key: 'code',              label: '💻 Code',        codeOnly: true }
  ];
  // HM sees HM Report tab (not Recruiter). Everyone else sees Recruiter tab (not HM Report).
  const tabs = allTabs.filter(t => {
    if (t.hmOnly)    return isHiringManager;
    if (t.nonHmOnly) return !isHiringManager;
    // Code tab only shown for live_coding interviews that have a submission
    if (t.codeOnly)  return interview.coding_mode === 'live_coding' && !!interview.code_submission;
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-8 py-6 shadow-sm sticky top-0 z-10">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-xl transition-all text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{interview.candidate_name}</h1>
              <p className="text-sm text-gray-500">{interview.role_type} • {interview.tech_stack} • {interview.interview_level}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(interview.status)}
            {hasScore && getRecBadge(interview.recommendation, interview.assessment?.verdict_reason)}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Message */}
        {message.text && (
          <div className={`rounded-xl p-4 mb-6 flex items-center gap-3 border-2 ${
            message.type === 'success' ? 'bg-green-50 border-green-400 text-green-700' : 'bg-red-50 border-red-400 text-red-700'
          }`}>
            <span className="text-xl">{message.type === 'success' ? '✅' : '❌'}</span>
            <p className="font-bold text-sm">{message.text}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          {status === 'scheduled' && (
            <button onClick={() => handleAction('send-invite', 'Send Invite')} disabled={!!actionLoading}
              className="px-5 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50">
              {actionLoading === 'send-invite' ? '⏳ Sending...' : '📧 Send Invite'}
            </button>
          )}
          {status === 'completed' && (
            // score-and-report: re-scoring always regenerates the reports, so they match the scores
            <button onClick={() => handleAction('score-and-report', 'Score')} disabled={!!actionLoading}
              className="px-5 py-2.5 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50">
              {actionLoading === 'score-and-report' ? '⏳ Scoring...' : '⭐ Score Interview'}
            </button>
          )}

          {(status === 'scheduled' || status === 'ready') && (
            <button onClick={() => handleAction('cancel', 'Cancel')} disabled={!!actionLoading}
              className="px-5 py-2.5 bg-red-100 text-red-700 font-bold rounded-xl hover:bg-red-200 transition-all border border-red-300 disabled:opacity-50">
              ✕ Cancel Interview
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 overflow-x-auto sticky top-[89px] z-10 shadow-sm">
          {tabs.map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2.5 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === tab.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ TAB: Overview ═══ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">

            {/* ── Score Hero — full width, most important ── */}
            {hasScore && (
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl shadow-lg p-6 text-white">
                <div className="flex flex-wrap items-center justify-between gap-6">
                  <div>
                    <p className="text-purple-200 text-sm font-semibold uppercase tracking-wide mb-1">Skill score</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-6xl font-black">{interview.overall_score}</span>
                      <span className="text-purple-300 text-xl font-semibold">/ 400</span>
                    </div>
                    {interview.assessment?.skill_band && (
                      <span className="inline-block mt-2 text-xs font-bold bg-white/20 px-3 py-1 rounded-lg" data-testid="skill-band">
                        Skill score band: {BAND_LABELS[interview.assessment.skill_band] || interview.assessment.skill_band}
                      </span>
                    )}
                    {interview.assessment?.percentiles_shown && interview.overall_percentile != null && (
                      <p className="text-purple-200 text-sm mt-1">{interview.overall_percentile}th percentile</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-3 max-w-md">
                    {getRecBadge(interview.recommendation, interview.assessment?.verdict_reason)}
                    {interview.assessment?.verdict_reason?.type === 'integrity_override' && (
                      <div className="text-sm text-purple-100 text-right" data-testid="verdict-reason">
                        <p>Reason: {interview.assessment.verdict_reason.trigger || interview.assessment.verdict_reason.detail}</p>
                        {(interview.assessment.signals || []).length > 0 && (
                          <p className="text-xs mt-1" data-testid="recorded-signals">
                            Recorded: {interview.assessment.signals.map((s) => (s.info_only ? `${s.label}: ${s.summary}` : `${s.label} ×${s.count}`)).join(', ')}
                          </p>
                        )}
                        {(interview.assessment.verdict_reason.not_monitored ?? 'face presence, leaving the camera frame, multiple faces') && (
                          <p className="text-xs opacity-70 mt-0.5">Not monitored: {interview.assessment.verdict_reason.not_monitored ?? 'face presence, leaving the camera frame, multiple faces'}.</p>
                        )}
                      </div>
                    )}
                    {interview.assessment?.verdict_reason?.monitoring_note && (
                      <p className="text-xs font-semibold text-amber-200 text-right" data-testid="monitoring-declined">
                        ⚠️ {interview.assessment.verdict_reason.monitoring_note} — verify key answers in the next round
                      </p>
                    )}
                    {interview.assessment?.verdict_reason?.review && (
                      <p className="text-xs text-amber-200 text-right max-w-md" data-testid="review-note">
                        ⚠️ {interview.assessment.verdict_reason.review}
                      </p>
                    )}
                    {interview.assessment?.verdict_reason?.coverage_note && (
                      <p className="text-xs text-amber-200 text-right" data-testid="coverage-note">
                        {interview.assessment.verdict_reason.coverage_note}
                      </p>
                    )}
                    <div className="flex gap-4 text-sm text-purple-200 text-right">
                      <IntegrityConfidence value={interview.final_confidence} />
                      <span className="whitespace-nowrap">Duration: {interview.actual_duration_minutes ? `${interview.actual_duration_minutes} min` : '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Candidate · Configuration · Schedule — single panel, 3 columns ── */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">

                {/* Candidate */}
                <div className="p-6">
                  <div className="flex items-center gap-2.5 mb-5">
                    <span className="block w-0.5 h-4 rounded-full bg-purple-500"></span>
                    <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Candidate</h3>
                  </div>
                  <div className="space-y-3">
                    <InfoRow label="Name"       value={interview.candidate_name} />
                    <InfoRow label="Email"      value={interview.candidate_email} />
                    <InfoRow label="Experience" value={interview.candidate_yoe ? `${interview.candidate_yoe} yrs` : '—'} />
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-50">
                    <span className="text-xs text-gray-400 font-medium block mb-2">Skills</span>
                    <p className="text-xs text-gray-700 leading-relaxed">{parseSkills(interview.candidate_skills)}</p>
                  </div>
                </div>

                {/* Configuration */}
                <div className="p-6">
                  <div className="flex items-center gap-2.5 mb-5">
                    <span className="block w-0.5 h-4 rounded-full bg-indigo-500"></span>
                    <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Configuration</h3>
                  </div>
                  <div className="space-y-3">
                    <InfoRow label="Role"        value={interview.role_type} />
                    <InfoRow label="Stack"       value={interview.tech_stack} />
                    <InfoRow label="Level"       value={interview.interview_level} />
                    <InfoRow label="Duration"    value={`${interview.duration_minutes || 60} min`} />
                    <InfoRow label="Coding Mode" value={interview.coding_mode?.replace('_', ' ')} />
                    <InfoRow label="Proctoring"  value={interview.proctoring_strictness} />
                    <InfoRow label="Passcode"    value={interview.passcode} mono />
                  </div>
                </div>

                {/* Schedule */}
                <div className="p-6">
                  <div className="flex items-center gap-2.5 mb-5">
                    <span className="block w-0.5 h-4 rounded-full bg-teal-500"></span>
                    <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Schedule</h3>
                  </div>
                  <div className="space-y-3">
                    <InfoRow label="Date"         value={interview.scheduled_datetime ? new Date(interview.scheduled_datetime).toLocaleString() : '—'} />
                    <InfoRow label="Invite Status" value={interview.invite_status || 'Not sent'} />
                    <InfoRow label="Invite Sent"   value={interview.invite_sent_at ? new Date(interview.invite_sent_at).toLocaleString() : '—'} />
                    <InfoRow label="Responded"     value={interview.invite_responded_at ? new Date(interview.invite_responded_at).toLocaleString() : '—'} />
                    {interview.hiring_manager_email && (
                      <InfoRow label="HM Email" value={interview.hiring_manager_email} />
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}


        {/* ═══ TAB: Transcript ═══ */}
        {activeTab === 'transcript' && (
          <TranscriptViewer transcript={transcript} candidateName={interview.candidate_name} />
        )}

        {/* ═══ TAB: HM Review ═══ */}
        {activeTab === 'hm-review' && (
          <div className="space-y-6">

            {/* ── Top row: Video (left ~60%) + Decision + Download (sticky right ~38%) ── */}
            <div className="flex gap-6 items-start">

              {/* Video player — left column */}
              <div className="flex-1 min-w-0">
                <RecordingPlayer
                  videoUrl={videoUrl}
                  audioUrl={audioUrl}
                  mergedUrl={mergedUrl}
                  mergePending={mergePending}
                  videoOffsetMs={videoOffsetMs}
                  isLoading={recordingLoading}
                  error={recordingError}
                  onRetry={fetchRecordings}
                  deleteAfter={interview.video_delete_after}
                  videoMissingReason={videoMissingReason}
                />
              </div>

              {/* Decision panel + Download — sticky right column */}
              <div className="w-[38%] flex-shrink-0 sticky top-[145px] space-y-4">

                {linkedAppId ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="decide-in-review">
                    <h3 className="text-sm font-bold text-gray-900 mb-1">Hiring Manager Decision</h3>
                    <p className="text-xs text-gray-500 mb-3">This screen belongs to a candidate in Quali5d — decide there, with the rest of their journey.</p>
                    <a href={`/applications/${linkedAppId}/review?tab=ai`} className="text-sm font-bold text-blue-600 hover:underline">Decide in the candidate’s review →</a>
                  </div>
                ) : <HMDecisionPanel
                  hmDecision={hmDecision}
                  hmComment={hmComment}
                  onDecisionChange={setHmDecision}
                  onCommentChange={setHmComment}
                  onSave={handleSaveHmDecision}
                  isSaving={hmDecisionSaving}
                  message={hmDecisionMessage}
                  lastUpdated={interview.hm_decision_updated_at}
                  isScored={!!interview.overall_score}
                  /* Wave 2: HM disagreement signal props */
                  hmAgreesWithAi={hmAgreesWithAi}
                  onAgreesWithAiChange={setHmAgreesWithAi}
                  hmDisagreementType={hmDisagreementType}
                  onDisagreementTypeChange={setHmDisagreementType}
                  hmWhatShouldHaveHappened={hmWhatShouldHaveHappened}
                  onWhatShouldHaveHappenedChange={setHmWhatShouldHaveHappened}
                  aiRecommendation={interview.recommendation}
                />}

                {/* Download Report */}
                {hmReport && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-sm">⬇️</div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">Download Report</h3>
                        <p className="text-xs text-gray-400">Export this interview report</p>
                      </div>
                    </div>
                    <ReportDownloadButtons report={hmReport} interview={interview} />
                  </div>
                )}

              </div>
            </div>

            {/* ── Full AI Report below ── */}
            <ReportView report={hmReport} type="Hiring Manager" interviewId={interview.id} apiBase={API_BASE}
                      questionSources={interview.question_sources} assessment={interview.assessment} />

          </div>
        )}

        {/* ═══ TAB: Recruiter Report ═══ */}
        {activeTab === 'recruiter-report' && (
          <ReportView report={recruiterReport} type="Recruiter" interviewId={interview.id} apiBase={API_BASE}
                      questionSources={interview.question_sources} assessment={interview.assessment} />
        )}

        {/* ═══ TAB: Code Submission ═══ */}
        {activeTab === 'code' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xl">💻</span>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Code Submission</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Submitted during live coding session</p>
                </div>
              </div>
              <span className="bg-purple-100 text-purple-700 text-xs font-bold px-3 py-1 rounded-full">
                {interview.coding_mode?.replace('_', ' ')}
              </span>
            </div>
            <div className="p-6">
              {(() => {
                if (!interview.code_submission) {
                  return (
                    <div className="text-center py-10">
                      <span className="text-4xl">📭</span>
                      <p className="text-gray-400 text-sm mt-3">No code submission recorded for this interview.</p>
                    </div>
                  );
                }
                // Try to parse as JSON array (new format)
                // Fall back to legacy plain text if not parseable
                let submissions = null;
                try {
                  const parsed = JSON.parse(interview.code_submission);
                  if (Array.isArray(parsed)) submissions = parsed;
                } catch (e) { /* legacy format */ }

                if (submissions) {
                  // ── New format: one collapsible card per problem ──
                  return (
                    <CodeSubmissionList submissions={submissions} />
                  );
                }
                // ── Legacy plain text format ──
                return (
                  <pre className="bg-gray-950 text-green-300 font-mono text-sm p-5 rounded-xl overflow-x-auto whitespace-pre-wrap break-words leading-relaxed" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {interview.code_submission}
                  </pre>
                );
              })()}
            </div>
          </div>
        )}
      </div>


    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// CODE SUBMISSION LIST — collapsible per-problem cards
// ═══════════════════════════════════════════════════════════════

function CodeSubmissionList({ submissions }) {
  const [expanded, setExpanded] = React.useState({});
  const toggle = (i) => setExpanded(prev => ({ ...prev, [i]: !prev[i] }));

  return (
    <div className="space-y-4">
      {submissions.map((sub, i) => (
        <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
          {/* Card header — always visible */}
          <button
            onClick={() => toggle(i)}
            className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          >
            <div className="flex items-center gap-3">
              <span className="bg-purple-600 text-white text-xs font-bold px-2.5 py-1 rounded-lg">Problem {sub.problem_number}</span>
              <span className="bg-gray-200 text-gray-700 text-xs font-mono font-semibold px-2.5 py-1 rounded-lg">{sub.language}</span>
              <span className="text-xs text-gray-400">
                {sub.code ? `${sub.code.split('\n').length} lines · ${sub.code.length} chars` : 'No code'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {sub.submitted_at && (
                <span className="text-xs text-gray-400 hidden sm:block">
                  {new Date(sub.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <span className="text-gray-400 text-sm">{expanded[i] ? '▲' : '▼'}</span>
            </div>
          </button>
          {/* Expanded code */}
          {expanded[i] && (
            <pre className="bg-gray-950 text-green-300 font-mono text-sm p-5 overflow-x-auto whitespace-pre-wrap break-words leading-relaxed" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {sub.code || '(no code written)'}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// TRANSCRIPT VIEWER
// ═══════════════════════════════════════════════════════════════

export function TranscriptViewer({ transcript, candidateName }) {
  const [searchTerm, setSearchTerm] = useState('');
  const transcriptEndRef = useRef(null);

  if (!transcript) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-16 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-400 border-t-transparent mx-auto mb-4"></div>
        <p className="text-gray-500">Loading transcript...</p>
      </div>
    );
  }

  if (!transcript.has_transcript || !transcript.turns || transcript.turns.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-16 text-center">
        <div className="text-6xl mb-4">💬</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">No Transcript Available</h3>
        <p className="text-gray-600">Transcript will be available once the interview is completed.</p>
      </div>
    );
  }

  const turns = transcript.turns;
  // Filter out empty turns (sendContextualUpdate calls stored as blank agent turns)
  const visibleTurns = turns.filter(t => t.message && t.message.trim().length > 0);
  const filteredTurns = searchTerm
    ? visibleTurns.filter(t => t.message?.toLowerCase().includes(searchTerm.toLowerCase()))
    : visibleTurns;

  const formatTimestamp = (secs) => {
    if (!secs && secs !== 0) return '';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-2xl font-black text-purple-600">{transcript.total_turns}</p>
              <p className="text-xs text-gray-500">Total Turns</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-blue-600">{transcript.candidate_turns}</p>
              <p className="text-xs text-gray-500">Candidate Turns</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-green-600">{transcript.total_turns - transcript.candidate_turns}</p>
              <p className="text-xs text-gray-500">AI Turns</p>
            </div>
          </div>
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search transcript..."
              className="pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-64"
            />
            <span className="absolute left-3 top-3 text-gray-400">🔍</span>
            {searchTerm && (
              <span className="absolute right-3 top-3 text-xs text-gray-400">
                {filteredTurns.length} matches
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Transcript Messages */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <h3 className="text-white font-bold">💬 Interview Transcript</h3>
          <p className="text-purple-200 text-xs mt-1">
            {candidateName} • {transcript.total_turns} exchanges
          </p>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {filteredTurns.map((turn, i) => {
            const isCandidate = turn.role === 'user' || turn.role === 'candidate';

            return (
              <div key={i} className={`flex ${isCandidate ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] ${
                  isCandidate
                    ? 'bg-purple-50 border-purple-200 rounded-2xl rounded-br-sm'
                    : 'bg-gray-50 border-gray-200 rounded-2xl rounded-bl-sm'
                } border p-4 shadow-sm`}>
                  {/* Speaker Header */}
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-xs font-bold ${isCandidate ? 'text-purple-600' : 'text-gray-500'}`}>
                      {isCandidate ? `👤 ${candidateName || 'Candidate'}` : '🤖 AI Interviewer'}
                    </span>
                    {turn.timestamp_seconds != null && (
                      <span className="text-xs text-gray-400 font-mono ml-3">
                        {formatTimestamp(turn.timestamp_seconds)}
                      </span>
                    )}
                  </div>
                  {/* Message */}
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {searchTerm ? highlightText(turn.message, searchTerm) : turn.message}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={transcriptEndRef} />
        </div>
      </div>
    </div>
  );
}

// Highlight search matches in transcript
function highlightText(text, term) {
  if (!term || !text) return text;
  const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase()
      ? <mark key={i} className="bg-yellow-200 rounded px-0.5">{part}</mark>
      : part
  );
}


// ═══════════════════════════════════════════════════════════════
// REPORT VIEWER (unchanged except InfoRow safety)
// ═══════════════════════════════════════════════════════════════

// ── Verdict / integrity display helpers (report consistency under integrity risk) ──
export const BAND_LABELS = { EXCEEDS_BAR: 'Exceeds', MEETS_BAR: 'Meets', BORDERLINE: 'Borderline', BELOW_BAR: 'Below' };

// "Below Bar — integrity" when an integrity override decided the verdict
export function verdictLabel(rec, reason) {
  const base = { EXCEEDS_BAR: 'Exceeds Bar', MEETS_BAR: 'Meets Bar', BORDERLINE: 'Borderline', BELOW_BAR: 'Below Bar' }[rec] || rec || '';
  return reason && reason.type === 'integrity_override' ? `${base} — integrity` : base;
}

// Bundle L — fairness: the AI is advisory; camera flags never decide the verdict (a human reviews them)
export const ADVISORY_FOOTER = 'AI assessment is advisory. Final hiring decisions are made by a human reviewer.';

export function reviewLabel(timeline) {
  const times = [...new Set((timeline || []).map((e) => e && e.start).filter(Boolean))];
  if (!times.length) return 'Needs human review';
  return `Needs human review — camera signals at ${times.slice(0, 5).join(', ')}${times.length > 5 ? ` (+${times.length - 5} more)` : ''}`;
}

// REVIEW_RECOMMENDED stays the risk level; when camera signals raised it, show what the human should review
export function riskLevelLabel(riskLevel, secondDevice) {
  return riskLevel === 'REVIEW_RECOMMENDED' && secondDevice?.review ? reviewLabel(secondDevice.timeline) : (riskLevel || '—');
}

export const NO_FRAME_NOTE = 'Assessed without the role frame. Comparison with other candidates may be inconsistent. Needs human review.';

// Bundle L: the role frame (JD + intake) the interview was scored on — and whether it has been rebuilt since
export function roleFrameText(rf) {
  if (rf && rf.missing) return rf.note || NO_FRAME_NOTE;
  if (!rf || !rf.version) return '';
  const base = `Role frame v${rf.version}${rf.level ? ` · level ${rf.level}` : ''}`;
  return rf.outdated
    ? `${base} — the role's frame has since been rebuilt (now v${rf.current_version}${rf.rebuilt_at ? `, ${rf.rebuilt_at.slice(0, 10)}` : ''}); compare with candidates on the same frame`
    : base;
}

export const INTEGRITY_CONFIDENCE_HELP =
  "How far the answers can be treated as the candidate's own, unassisted work, based on proctoring "
  + 'signals (tab switches, leaving the camera frame, paste, DevTools). A clean interview shows 90%; '
  + 'below 50% is high risk.';

export function IntegrityConfidence({ value, className = '' }) {
  if (value == null) return null;
  const pct = Math.round(Number(value) * 100);
  return (
    <span className={className} title={INTEGRITY_CONFIDENCE_HELP} data-testid="integrity-confidence">
      Integrity confidence: {pct}%
      <span className="block text-xs opacity-80 mt-0.5">{INTEGRITY_CONFIDENCE_HELP}</span>
    </span>
  );
}

export function EvidenceBanner({ status }) {
  if (status !== 'unverified' && status !== 'verify_next_round') return null;
  const unverified = status === 'unverified';
  return (
    <div data-testid="evidence-banner"
         className={`rounded-xl px-4 py-3 border text-sm font-semibold ${unverified ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
      {unverified
        ? '⚠️ Unverified — possible assistance. Integrity signals were high, so the skills, strengths and red-flag results below describe what the candidate said, not verified ability.'
        : 'ℹ️ Some integrity signals were raised — verify the key strengths below in the next round.'}
    </div>
  );
}

// ── Report download (PDF / DOCX) — icon buttons; the exporter is loaded on demand ──
const PdfIcon = () => (
  <svg viewBox="0 0 32 32" className="w-7 h-7" aria-hidden="true" focusable="false">
    <path d="M7 2h12l7 7v19a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" fill="#fff" stroke="#DC2626" strokeWidth="1.6" />
    <path d="M19 2v7h7" fill="none" stroke="#DC2626" strokeWidth="1.6" />
    <rect x="3" y="15" width="20" height="9" rx="1.5" fill="#DC2626" />
    <text x="13" y="22" textAnchor="middle" fontSize="7" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">PDF</text>
  </svg>
);

const WordIcon = () => (
  <svg viewBox="0 0 32 32" className="w-7 h-7" aria-hidden="true" focusable="false">
    <path d="M7 2h12l7 7v19a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" fill="#fff" stroke="#2B579A" strokeWidth="1.6" />
    <path d="M19 2v7h7" fill="none" stroke="#2B579A" strokeWidth="1.6" />
    <rect x="3" y="15" width="20" height="9" rx="1.5" fill="#2B579A" />
    <text x="13" y="22" textAnchor="middle" fontSize="6.2" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">DOCX</text>
  </svg>
);

export function ReportDownloadButtons({ report, interview, typeLabel = 'Hiring Manager' }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const run = async (format) => {
    setBusy(format);
    setError('');
    try {
      const { downloadReport } = await import('../utils/reportExport');
      await downloadReport(format, { report, interview, typeLabel });
    } catch (e) {
      console.error('Report download failed', e);
      setError(`Couldn't create the ${format.toUpperCase()} file. Please try again.`);
    } finally {
      setBusy('');
    }
  };
  const buttons = [
    { format: 'pdf', label: 'Download report as PDF', Icon: PdfIcon },
    { format: 'docx', label: 'Download report as Word (DOCX)', Icon: WordIcon },
  ];
  return (
    <div>
      <div className="flex gap-3" role="group" aria-label="Download report">
        {buttons.map(({ format, label, Icon }) => (
          <div key={format} className="relative group">
            <button
              type="button"
              onClick={() => run(format)}
              disabled={!!busy}
              aria-label={label}
              aria-describedby={`report-dl-tip-${format}`}
              aria-busy={busy === format}
              data-testid={`report-download-${format}`}
              className="w-12 h-12 flex items-center justify-center rounded-xl border-2 border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0066CC] disabled:opacity-50 disabled:cursor-wait transition-all"
            >
              {busy === format
                ? <span className="w-5 h-5 border-2 border-gray-300 border-t-[#0066CC] rounded-full animate-spin" aria-hidden="true" />
                : <Icon />}
            </button>
            <span
              id={`report-dl-tip-${format}`}
              role="tooltip"
              className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity z-10"
            >
              {busy === format ? 'Preparing…' : label}
            </span>
          </div>
        ))}
      </div>
      {error && <p className="mt-3 text-xs font-semibold text-red-600" role="alert">{error}</p>}
    </div>
  );
}

// "Why this question" — role requirement + HM priority + candidate angle (from the blueprint's sources)
export function WhyThisQuestion({ source }) {
  if (!source) return null;
  const req = source.role_requirements || [];
  const hm = source.hm_priorities || [];
  const angle = source.candidate_angle || {};
  if (!source.rationale && !req.length && !hm.length && !angle.resume_evidence) return null;
  const strip = (s) => String(s).replace(/^\s*[a-z_ -]+:\s*/i, '');
  const angleLabel = angle.kind === 'gap_to_probe' ? 'Gap to probe'
    : angle.kind === 'claim_to_verify' ? 'Claim to verify' : 'Candidate angle';
  const warn = source.flagged || source.no_candidate_angle;
  return (
    <div className={`rounded-xl px-3 py-2 mb-3 border space-y-1 ${warn ? 'bg-amber-50 border-amber-200' : 'bg-indigo-50 border-indigo-100'}`}
         data-testid="why-this-question">
      <p className="text-xs text-indigo-800"><span className="font-bold">Why this question: </span>{source.rationale}</p>
      {req.length > 0 && (
        <p className="text-xs text-indigo-700"><span className="font-semibold">Role requirement: </span>{req.map(strip).join(' · ')}</p>
      )}
      {hm.length > 0 && (
        <p className="text-xs text-indigo-700"><span className="font-semibold">HM priority: </span>{hm.map(strip).join(' · ')}</p>
      )}
      {angle.resume_evidence && (
        <p className="text-xs text-indigo-700">
          <span className="font-semibold">{angleLabel}: </span>{strip(angle.resume_evidence)}
          {angle.how_targeted ? ` — ${angle.how_targeted}` : ''}
        </p>
      )}
      {source.flagged && (
        <p className="text-xs font-semibold text-amber-800">⚠️ This question doesn't cite a JD, intake or tech-stack requirement.</p>
      )}
      {source.no_candidate_angle && (
        <p className="text-xs text-amber-800">Note: not tailored to this candidate's resume (no gap or claim targeted).</p>
      )}
    </div>
  );
}

export function ReportView({ report, type, interviewId, apiBase, questionSources, assessment }) {
  const [regenerating, setRegenerating] = React.useState(false);
  const [regenMsg, setRegenMsg] = React.useState('');

  const handleRegenerate = async () => {
    setRegenerating(true);
    setRegenMsg('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/phase3/interview/${interviewId}/generate-reports`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        setRegenMsg('✅ Report generated! Refresh the page to view it.');
      } else {
        const err = await res.json().catch(() => ({}));
        setRegenMsg(`❌ Failed: ${err.detail || res.status}`);
      }
    } catch(e) {
      setRegenMsg('❌ Connection error. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  if (!report) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-16 text-center">
        <div className="text-6xl mb-4">📋</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{type} Report Not Generated</h3>
        <p className="text-gray-600 mb-6">The report for this interview has not been generated yet.</p>
        {interviewId && (
          <div>
            <button onClick={handleRegenerate} disabled={regenerating}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl transition-all disabled:opacity-60 text-sm">
              {regenerating ? (
                <span className="flex items-center gap-2"><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Generating...</span>
              ) : '🔄 Generate Report'}
            </button>
            {regenMsg && <p className="mt-4 text-sm font-medium text-gray-700">{regenMsg}</p>}
          </div>
        )}
      </div>
    );
  }

  let content = {};
  try {
    if (typeof report.report_content === 'string') {
      content = JSON.parse(report.report_content);
    } else if (report.report_content && typeof report.report_content === 'object') {
      content = report.report_content;
    }
  } catch(e) {
    console.error('ReportView: failed to parse report_content', e);
  }

  const isHM = type === 'Hiring Manager';

  // ── Score bar helper ──
  const getScoreZone = (score) => {
    if (score >= 320) return { label: 'EXCEEDS BAR', color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' };
    if (score >= 200) return { label: 'MEETS BAR',   color: '#2563eb', bg: '#eff6ff', border: '#93c5fd' };
    if (score >= 150) return { label: 'BORDERLINE',  color: '#d97706', bg: '#fffbeb', border: '#fcd34d' };
    return                     { label: 'BELOW BAR',  color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' };
  };

  const sb = content.score_bar || {};
  const score = sb.overall_score ?? content.headline?.match(/\d+/)?.[0] ?? 0;
  const zone = getScoreZone(Number(score));
  const pb = content.performance_benchmarking || {};
  // Report-level verdict context (stamped by the backend; the interview's assessment for older reports)
  const evidence = content.evidence_status || assessment?.evidence_status;
  const verdictReason = content.verdict_reason || assessment?.verdict_reason;
  const band = content.skill_band || assessment?.skill_band
    || { 'EXCEEDS BAR': 'EXCEEDS_BAR', 'MEETS BAR': 'MEETS_BAR', BORDERLINE: 'BORDERLINE', 'BELOW BAR': 'BELOW_BAR' }[zone.label];
  const showPercentiles = (content.percentiles_shown ?? assessment?.percentiles_shown ?? true) && pb.shown !== false;
  const verdictRec = sb.recommendation || null;

  return (
    <div className="space-y-5">

      {/* ══ 1. HERO HEADER ══ */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #312e81 100%)' }}
        className="rounded-2xl p-7 text-white shadow-xl">
        {/* Headline */}
        {content.headline && (
          <h2 className="text-xl font-black tracking-tight mb-1">{content.headline}</h2>
        )}
        {content.executive_summary && (
          <p className="text-slate-300 text-sm leading-relaxed mb-5">{content.executive_summary}</p>
        )}

        {/* Score Bar */}
        <div className="bg-white/10 rounded-xl p-4 mb-4">
          <div className="flex items-end justify-between mb-2">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mb-1">Overall Score</p>
              <p className="text-5xl font-black" style={{ color: zone.color }}>
                {sb.overall_score ?? score}
                <span className="text-xl text-slate-400 font-normal"> / 400</span>
              </p>
            </div>
            <div className="text-right">
              {verdictRec && (
                <p className="text-sm font-black text-white mb-1" data-testid="report-verdict">
                  Verdict: {verdictLabel(verdictRec, verdictReason)}
                </p>
              )}
              {verdictReason?.type === 'integrity_override' && (
                <p className="text-xs text-red-200 mb-2 max-w-[320px] ml-auto" data-testid="report-verdict-reason">
                  Reason: {verdictReason.detail}
                </p>
              )}
              {verdictReason?.coverage_note && (
                <p className="text-xs text-amber-200 mb-2 max-w-[320px] ml-auto">{verdictReason.coverage_note}</p>
              )}
              <span className="inline-block px-3 py-1.5 rounded-lg text-xs font-bold"
                style={{ background: zone.bg, color: zone.color, border: `1px solid ${zone.border}` }}
                data-testid="report-skill-band">
                Skill score band: {BAND_LABELS[band] || zone.label}
              </span>
              {content.recommendation_rationale && (
                <p className="text-xs text-slate-400 mt-2 max-w-[220px]">
                  {content.recommendation_rationale.substring(0, 100)}...
                </p>
              )}
            </div>
          </div>
          {/* Visual score bar with thresholds */}
          <div className="relative h-4 bg-white/10 rounded-full overflow-hidden mt-3">
            <div className="absolute h-full rounded-full transition-all"
              style={{ width: `${Math.min((Number(sb.overall_score ?? score) / 400) * 100, 100)}%`,
                background: `linear-gradient(90deg, ${zone.color}99, ${zone.color})` }} />
            {/* Threshold markers */}
            {[{pct: 37.3, label:'BB'}, {pct: 50, label:'BL'}, {pct: 80, label:'MB'}].map((m,i) => (
              <div key={i} className="absolute top-0 h-full w-0.5 bg-white/30"
                style={{ left: `${m.pct}%` }} />
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1 px-0.5">
            <span>0</span><span>150</span><span>200</span><span className="ml-8">320</span><span>400</span>
          </div>
        </div>

        {/* Benchmarking pills — only with a comparison group of 10+ */}
        {showPercentiles && (pb.role_percentile || pb.all_roles_percentile) && (
          <div className="flex gap-3 flex-wrap">
            <div className="bg-white/10 rounded-xl px-4 py-2 text-center">
              <p className="text-2xl font-black text-white">{pb.role_percentile ?? '—'}<span className="text-sm font-normal text-slate-400">th</span></p>
              <p className="text-xs text-slate-400">Role Percentile</p>
              {pb.role_cohort_size > 0 && <p className="text-xs text-slate-500">vs {pb.role_cohort_size} candidates</p>}
            </div>
            <div className="bg-white/10 rounded-xl px-4 py-2 text-center">
              <p className="text-2xl font-black text-white">{pb.all_roles_percentile ?? '—'}<span className="text-sm font-normal text-slate-400">th</span></p>
              <p className="text-xs text-slate-400">All-IT Percentile</p>
              {pb.all_roles_cohort_size > 0 && <p className="text-xs text-slate-500">vs {pb.all_roles_cohort_size} candidates</p>}
            </div>
            {pb.benchmarking_note && (
              <div className="flex-1 bg-white/5 rounded-xl px-4 py-2 flex items-center">
                <p className="text-xs text-slate-300 italic">{pb.benchmarking_note}</p>
              </div>
            )}
          </div>
        )}
        {!showPercentiles && (
          <p className="text-xs text-slate-400" data-testid="percentiles-hidden">
            Percentiles hidden — fewer than 10 comparable candidates so far.
          </p>
        )}
      </div>

      <EvidenceBanner status={evidence} />

      {(content.role_frame || assessment?.role_frame)?.missing && (
        <div className="rounded-xl px-4 py-3 border text-sm font-semibold bg-amber-50 border-amber-300 text-amber-900"
             data-testid="no-frame">
          ⚠️ {(content.role_frame || assessment?.role_frame).note || NO_FRAME_NOTE}
        </div>
      )}

      {(content.human_review || content.fairness_check?.passed === false) && (
        <div className="rounded-xl px-4 py-3 border text-sm font-semibold bg-amber-50 border-amber-300 text-amber-900"
             data-testid="fairness-review">
          ⚠️ {content.human_review || 'Needs human review — protected-characteristic wording was removed from this AI report.'}
        </div>
      )}

      {/* ══ 2. CANDIDATE PROFILE ══ */}
      {content.candidate_profile_summary && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-lg">👤</div>
            <h3 className="text-base font-bold text-gray-900">Candidate Profile</h3>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">{content.candidate_profile_summary}</p>
        </div>
      )}

      {/* ══ 3. AREAS ASSESSED TABLE (Karat-style) ══ */}
      {content.areas_assessed?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center text-lg">📊</div>
            <h3 className="text-base font-bold text-gray-900">Areas Assessed</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Area', 'Score', 'Points', 'Weight', ...(showPercentiles ? ['Role %ile', 'All-IT %ile'] : []), 'Assessment'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {content.areas_assessed.map((area, i) => {
                  const s = Number(area.score_0_5 ?? 0);
                  const pct = (s / 5) * 100;
                  const barColor = pct >= 70 ? '#059669' : pct >= 50 ? '#2563eb' : pct >= 30 ? '#d97706' : '#dc2626';
                  return (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{area.area_name}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-sm" style={{ color: barColor }}>{s.toFixed(1)}</span>
                          <span className="text-gray-400 text-xs">/5</span>
                          <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: barColor }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        <span className="font-bold text-gray-900">{area.achieved_points ?? '—'}</span>
                        <span className="text-gray-400"> / {area.allocated_points ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {area.weight_pct != null ? `${area.weight_pct}%` : '—'}
                      </td>
                      {showPercentiles && (
                        <td className="px-4 py-3 text-xs font-bold text-indigo-600 whitespace-nowrap">
                          {area.role_percentile != null ? `${area.role_percentile}th` : '—'}
                        </td>
                      )}
                      {showPercentiles && (
                        <td className="px-4 py-3 text-xs font-bold text-blue-600 whitespace-nowrap">
                          {area.all_roles_percentile != null ? `${area.all_roles_percentile}th` : '—'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-[220px]">{area.area_assessment ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ 4. SUMMARY — strengths + weaknesses ══ */}
      {content.summary && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center text-lg">🧠</div>
            <h3 className="text-base font-bold text-gray-900">Interview Summary</h3>
          </div>
          {content.summary.full_summary && (
            <p className="text-sm text-gray-700 leading-relaxed">{content.summary.full_summary}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {content.summary.strengths?.length > 0 && (
              <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-3">💪 Strengths</p>
                <div className="space-y-3">
                  {content.summary.strengths.map((s, i) => (
                    <div key={i}>
                      <p className="text-sm font-semibold text-green-900">{s.area || s}</p>
                      {(s.verification || evidence === 'unverified') && (
                        <p className="text-xs font-bold text-red-700 mt-0.5" data-testid="strength-unverified">
                          {s.verification || 'unverified — possible assistance'}
                        </p>
                      )}
                      {s.evidence && <p className="text-xs text-green-700 mt-0.5 italic">"{s.evidence}"</p>}
                      {s.impact && <p className="text-xs text-green-600 mt-0.5">→ {s.impact}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {content.summary.weaknesses?.length > 0 && (
              <div className="bg-orange-50 rounded-xl border border-orange-200 p-4">
                <p className="text-xs font-bold text-orange-700 uppercase tracking-wide mb-3">⚠️ Weaknesses</p>
                <div className="space-y-3">
                  {content.summary.weaknesses.map((w, i) => (
                    <div key={i}>
                      <p className="text-sm font-semibold text-orange-900">{w.area || w}</p>
                      {w.evidence && <p className="text-xs text-orange-700 mt-0.5 italic">"{w.evidence}"</p>}
                      {w.context && <p className="text-xs text-orange-600 mt-0.5">Context: {w.context}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ 4b. NOT ASSESSED — planned topics never asked (not weaknesses) ══ */}
      {content.not_assessed?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-testid="not-assessed">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-lg">➖</div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Not assessed</h3>
              <p className="text-xs text-gray-400">Not asked in this interview — neither a strength nor a weakness</p>
            </div>
          </div>
          <ul className="space-y-1.5">
            {content.not_assessed.map((n, i) => (
              <li key={i} className="text-sm text-gray-700">
                <span className="font-semibold">{n.topic || n}</span>
                {n.note && <span className="text-gray-500"> — {n.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ══ 4d. SECOND-DEVICE — camera / answer-pattern signals with times to review in the recording ══ */}
      {(content.second_device?.summary || content.second_device?.not_monitored) && (
        <div className={`bg-white rounded-2xl shadow-sm border p-6 ${content.second_device.review ? 'border-amber-300' : 'border-gray-200'}`}
             data-testid="second-device">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center text-lg">📱</div>
            <div>
              <h3 className="text-base font-bold text-gray-900">
                {content.second_device.review ? reviewLabel(content.second_device.timeline)
                  : content.second_device.declined ? 'Second-device monitoring declined by candidate'
                  : 'Camera and answer signals (information only)'}
              </h3>
              <p className="text-xs text-gray-400">Not proof — review the recording at these times</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 mb-2">{content.second_device.summary}</p>
          {(content.second_device.timeline || []).length > 0 && (
            <ul className="space-y-1">
              {content.second_device.timeline.map((e, i) => (
                <li key={i} className="text-sm text-gray-700">
                  <span className="font-mono text-xs text-gray-400 mr-2">{e.start}–{e.end}</span>
                  {e.label} ({e.duration_s} s{e.confidence ? `, confidence ${Math.round(e.confidence * 100)}%` : ''})
                </li>
              ))}
            </ul>
          )}
          {content.second_device.not_monitored && (
            <p className="text-xs text-gray-400 mt-2">Not monitored: {content.second_device.not_monitored}</p>
          )}
        </div>
      )}

      {/* ══ 4c. INTERRUPTED — the interviewer moved on before a real answer (not weaknesses) ══ */}
      {content.interrupted?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-6" data-testid="interrupted">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center text-lg">⏸️</div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Interrupted by the interviewer — not fully assessed</h3>
              <p className="text-xs text-gray-400">The interviewer moved on before the candidate finished — not scored as a missing answer</p>
            </div>
          </div>
          <ul className="space-y-1.5">
            {content.interrupted.map((it, i) => (
              <li key={i} className="text-sm text-gray-700">
                <span className="font-mono text-xs text-gray-400 mr-2">{it.time}</span>
                <span className="font-semibold">{it.question}</span>
                {it.why && <span className="text-gray-500"> — {it.why}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ══ 5. PER-PROBLEM BREAKDOWN (core Karat-style) ══ */}
      {content.problem_breakdown?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-yellow-100 flex items-center justify-center text-lg">🧩</div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Problem Breakdown</h3>
              <p className="text-xs text-gray-400">Per-question analysis with criteria met / missed</p>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {content.problem_breakdown.map((p, i) => {
              const ps = Number(p.problem_score ?? 0);
              const ppct = (ps / 5) * 100;
              const pcolor = ppct >= 70 ? '#059669' : ppct >= 50 ? '#2563eb' : ppct >= 30 ? '#d97706' : '#dc2626';
              return (
                <div key={i} className="p-6">
                  {/* Problem header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center text-sm font-black flex-shrink-0">
                        {p.problem_number ?? i+1}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">{p.category}</p>
                        {p.question_asked && (
                          <p className="text-sm font-semibold text-gray-800 mt-0.5">"{p.question_asked}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <div className="text-right">
                        <span className="text-2xl font-black" style={{ color: pcolor }}>{ps.toFixed(1)}</span>
                        <span className="text-xs text-gray-400">/5</span>
                      </div>
                      <div className="w-2 h-12 bg-gray-100 rounded-full overflow-hidden">
                        <div className="w-full rounded-full transition-all" style={{
                          height: `${ppct}%`, background: pcolor, marginTop: `${100-ppct}%`
                        }} />
                      </div>
                    </div>
                  </div>

                  <WhyThisQuestion source={(questionSources || []).find(
                    (q) => String(q.problem_number) === String(p.problem_number ?? i + 1))} />

                  {/* Criteria met / missed — the Karat checkbox section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    {p.criteria_met?.length > 0 && (
                      <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                        <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">✓ Criteria Met</p>
                        <ul className="space-y-1">
                          {p.criteria_met.map((c, j) => (
                            <li key={j} className="flex items-start gap-1.5">
                              <span className="text-green-500 font-bold mt-0.5 text-xs flex-shrink-0">✓</span>
                              <span className="text-xs text-green-800">{c}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {p.criteria_missed?.length > 0 && (
                      <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                        <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-2">✗ Criteria Missed</p>
                        <ul className="space-y-1">
                          {p.criteria_missed.map((c, j) => (
                            <li key={j} className="flex items-start gap-1.5">
                              <span className="text-red-500 font-bold mt-0.5 text-xs flex-shrink-0">✗</span>
                              <span className="text-xs text-red-800">{c}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Interviewer thoughts */}
                  {p.interviewer_thoughts && (
                    <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 mb-3">
                      <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">🤖 Interviewer Observations</p>
                      <p className="text-sm text-slate-700 leading-relaxed">{p.interviewer_thoughts}</p>
                    </div>
                  )}

                  {/* Key quote */}
                  {p.key_quote && (
                    <div className="border-l-4 border-indigo-300 pl-4 py-1">
                      <p className="text-xs font-bold text-indigo-400 uppercase tracking-wide mb-1">Key Quote</p>
                      <p className="text-sm text-indigo-800 italic">"{p.key_quote}"</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ 6. RISKS ══ */}
      {content.risks?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center text-lg">⚠️</div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Risk Assessment</h3>
              <p className="text-xs text-gray-400">Each risk includes mitigability and a next-round probe</p>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {content.risks.map((r, i) => {
              const mit = (r.mitigability || r.risk_area || '').toUpperCase();
              const mitConfig = mit.includes('HIGH')
                ? { cls: 'bg-green-100 text-green-700', label: 'HIGH mitigability' }
                : mit.includes('MEDIUM') || mit.includes('MED')
                ? { cls: 'bg-yellow-100 text-yellow-700', label: 'MEDIUM mitigability' }
                : { cls: 'bg-red-100 text-red-700', label: 'LOW mitigability' };
              return (
                <div key={i} className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="font-bold text-gray-900 text-sm">{r.risk_area || r.area}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0 ${mitConfig.cls}`}>
                      {mitConfig.label}
                    </span>
                  </div>
                  {r.evidence && (
                    <p className="text-sm text-gray-600 mb-2 italic">"{r.evidence}"</p>
                  )}
                  {r.mitigability_rationale && (
                    <p className="text-xs text-gray-500 mb-2">{r.mitigability_rationale}</p>
                  )}
                  {r.next_round_probe && (
                    <div className="bg-indigo-50 rounded-lg px-3 py-2 border border-indigo-200">
                      <p className="text-xs font-bold text-indigo-600 mb-0.5">💬 Next Round Probe</p>
                      <p className="text-xs text-indigo-800">"{r.next_round_probe}"</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ 7. RED FLAG ASSESSMENT ══ */}
      {content.red_flag_assessment?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-rose-100 flex items-center justify-center text-lg">🚩</div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Red Flag Assessment</h3>
              <p className="text-xs text-gray-400">Integrity and behavioural signals from the interview</p>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {content.red_flag_assessment.map((f, i) => {
              const rawSev = (f.severity || '').toUpperCase();
              // Answers given under integrity risk can't clear a red flag (also covers older reports)
              const sev = evidence === 'unverified' && rawSev === 'CLEARED' ? 'UNVERIFIED' : rawSev;
              const sevConfig =
                sev === 'UNVERIFIED'   ? { cls: 'bg-gray-200 text-gray-800',  icon: '❔' } :
                sev === 'NOT_ASSESSED' ? { cls: 'bg-gray-100 text-gray-600',  icon: '➖' } :
                sev === 'CLEARED'  ? { cls: 'bg-green-100 text-green-700',  icon: '✅' } :
                sev === 'LOW'      ? { cls: 'bg-blue-100 text-blue-700',    icon: '🔵' } :
                sev === 'MEDIUM'   ? { cls: 'bg-yellow-100 text-yellow-700',icon: '⚠️' } :
                                    { cls: 'bg-red-100 text-red-700',       icon: '🔴' };
              return (
                <div key={i} className="p-5">
                  <div className="flex items-start gap-3">
                    <span className="text-lg flex-shrink-0">{sevConfig.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-gray-900 text-sm">{f.flag}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${sevConfig.cls}`}>{sev}</span>
                        {f.observed === false && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">NOT OBSERVED</span>
                        )}
                      </div>
                      {f.evidence && <p className="text-xs text-gray-600 mb-1 italic">"{f.evidence}"</p>}
                      {f.recommendation && (
                        <p className="text-xs text-gray-500">→ {f.recommendation}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ 8. NEXT ROUND GUIDANCE ══ */}
      {content.next_round_guidance?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-lg">🎯</div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Next Round Guidance</h3>
              <p className="text-xs text-gray-400">Specific questions to ask in the next interview</p>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {content.next_round_guidance.map((g, i) => (
              <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                {g.area && <p className="text-xs font-bold text-indigo-500 uppercase tracking-wide mb-1">{g.area}</p>}
                <p className="text-sm font-semibold text-indigo-900">
                  {g.specific_question || (typeof g === 'string' ? g : '')}
                </p>
                {g.why && <p className="text-xs text-indigo-600 mt-1">Why: {g.why}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ 9. INTEGRITY + RATIONALE ══ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {content.integrity_assessment && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-lg">🔒</div>
              <h3 className="text-base font-bold text-gray-900">Integrity Assessment</h3>
            </div>
            <div className="space-y-2 mb-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500" title={INTEGRITY_CONFIDENCE_HELP}>Integrity confidence</span>
                <span className="text-sm font-bold text-gray-900">
                  {content.integrity_assessment.confidence_level}
                </span>
              </div>
              <p className="text-xs text-gray-400" data-testid="integrity-confidence-help">{INTEGRITY_CONFIDENCE_HELP}</p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Risk Level</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  content.integrity_assessment.risk_level === 'CLEAN' ? 'bg-green-100 text-green-700' :
                  content.integrity_assessment.risk_level === 'REVIEW_RECOMMENDED' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {riskLevelLabel(content.integrity_assessment.risk_level, content.second_device)}
                </span>
              </div>
            </div>
            {content.integrity_assessment.proctoring_summary && (
              <p className="text-xs text-gray-500 leading-relaxed">
                {content.integrity_assessment.proctoring_summary}
              </p>
            )}
          </div>
        )}
        {content.recommendation_rationale && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center text-lg">📝</div>
              <h3 className="text-base font-bold text-gray-900">Recommendation Rationale</h3>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{content.recommendation_rationale}</p>
          </div>
        )}
      </div>

      {/* ══ RECRUITER-ONLY sections ══ */}
      {!isHM && content.role_fit_summary && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center text-lg">🎯</div>
            <h3 className="text-base font-bold text-gray-900">Role Fit</h3>
          </div>
          <p className="text-sm text-gray-700">{content.role_fit_summary}</p>
          {content.suggested_next_steps && (
            <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm font-semibold text-blue-800">Next Steps</p>
              <p className="text-sm text-blue-700 mt-1">{content.suggested_next_steps}</p>
            </div>
          )}
        </div>
      )}
      {!isHM && content.talking_points_for_hm?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center text-lg">💬</div>
            <h3 className="text-base font-bold text-gray-900">HM Talking Points</h3>
          </div>
          <ul className="space-y-2">
            {content.talking_points_for_hm.map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-teal-500 mt-0.5">•</span>
                <span className="text-sm text-gray-700">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isHM && !(content.role_frame || assessment?.role_frame)?.missing && roleFrameText(content.role_frame || assessment?.role_frame) && (
        <p className={`text-xs text-center ${(content.role_frame || assessment?.role_frame)?.outdated ? 'text-amber-700 font-semibold' : 'text-gray-400'}`}
           data-testid="role-frame">
          {roleFrameText(content.role_frame || assessment?.role_frame)}
        </p>
      )}
      {isHM && (
        <p className="text-xs text-gray-500 italic text-center border-t border-gray-100 pt-3" data-testid="advisory-footer">
          {ADVISORY_FOOTER}
        </p>
      )}

      {/* Timestamp */}
      {report.created_at && (
        <p className="text-xs text-gray-400 text-right pb-2">
          Report generated: {new Date(report.created_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

// ── Helper: Info Row
function InfoRow({ label, value, mono }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-gray-500 font-medium">{label}</span>
      <span className={`text-sm text-gray-900 font-semibold ${mono ? 'font-mono bg-gray-100 px-2 py-0.5 rounded' : ''}`}>
        {value || '—'}
      </span>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// HM DECISION PANEL  (HM only — sits between recording and report)
// ═══════════════════════════════════════════════════════════════

const DECISION_OPTIONS = [
  {
    value: 'move_forward',
    label: 'Move Forward',
    icon: '✅',
    color: 'border-green-400 bg-green-50 text-green-800',
    activeColor: 'border-green-500 bg-green-500 text-white shadow-lg'
  },
  {
    value: 'on_hold',
    label: 'On Hold',
    icon: '⏸️',
    color: 'border-yellow-400 bg-yellow-50 text-yellow-800',
    activeColor: 'border-yellow-500 bg-yellow-500 text-white shadow-lg'
  },
  {
    value: 'rejected',
    label: 'Rejected',
    icon: '❌',
    color: 'border-red-300 bg-red-50 text-red-800',
    activeColor: 'border-red-500 bg-red-500 text-white shadow-lg'
  }
];

// ═══════════════════════════════════════════════════════════════
// WAVE 2 — Disagreement type options (for AI learning loop)
// ═══════════════════════════════════════════════════════════════

const DISAGREEMENT_TYPES = [
  { value: 'missing_question',     label: 'Should have asked something' },
  { value: 'incorrect_score',      label: 'Scored an answer wrong' },
  { value: 'wrong_recommendation', label: 'Final recommendation was off' },
  { value: 'other',                label: 'Something else' },
];

function HMDecisionPanel({
  hmDecision, hmComment,
  onDecisionChange, onCommentChange,
  onSave, isSaving, message,
  lastUpdated, isScored,
  // Wave 2: disagreement signal props
  hmAgreesWithAi, onAgreesWithAiChange,
  hmDisagreementType, onDisagreementTypeChange,
  hmWhatShouldHaveHappened, onWhatShouldHaveHappenedChange,
  aiRecommendation,
}) {
  if (!isScored) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 text-center">
        <div className="text-4xl mb-2">⏳</div>
        <p className="text-gray-500 text-sm">Interview must be scored before you can submit a decision.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">👔</span>
          <div>
            <h3 className="text-base font-bold text-gray-900">Your Decision</h3>
            <p className="text-xs text-gray-400">Submit your hiring decision for this candidate</p>
          </div>
        </div>
        {lastUpdated && (
          <p className="text-xs text-gray-400">
            Last updated: {new Date(lastUpdated).toLocaleString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
              hour: '2-digit', minute: '2-digit', hour12: true
            })}
          </p>
        )}
      </div>

      <div className="p-6 space-y-5">
        {/* Decision buttons */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-3">Candidate Status</p>
          <div className="grid grid-cols-3 gap-3">
            {DECISION_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => onDecisionChange(opt.value)}
                className={`flex flex-col items-center gap-2 py-4 px-3 rounded-xl border-2 font-bold text-sm transition-all ${
                  hmDecision === opt.value ? opt.activeColor : opt.color + ' hover:opacity-80'
                }`}
              >
                <span className="text-2xl">{opt.icon}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Comment box */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Reason / Comment <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={hmComment}
            onChange={e => onCommentChange(e.target.value)}
            placeholder="Add context for the recruiter — e.g. strong systems design but needs more cloud experience..."
            rows={3}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
          />
        </div>

        {/* ─── Wave 2 — Help us improve AI section ─────────────────────── */}
        <div className="border-t-2 border-dashed border-gray-200 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-base">🎯</span>
            <p className="text-sm font-bold text-gray-800">Help us improve Alex (optional)</p>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Did the AI's recommendation
            {aiRecommendation && (
              <span className="font-semibold text-gray-700"> ({String(aiRecommendation).toLowerCase().replace(/_/g, ' ')})</span>
            )}
            {' '}match your judgment of this candidate?
          </p>

          {/* Agree / Disagree toggle */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => onAgreesWithAiChange(true)}
              className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                hmAgreesWithAi === true
                  ? 'border-emerald-500 bg-emerald-500 text-white shadow'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:opacity-80'
              }`}
            >
              👍 I agree with AI
            </button>
            <button
              onClick={() => onAgreesWithAiChange(false)}
              className={`py-2.5 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                hmAgreesWithAi === false
                  ? 'border-orange-500 bg-orange-500 text-white shadow'
                  : 'border-orange-200 bg-orange-50 text-orange-700 hover:opacity-80'
              }`}
            >
              👎 I disagree
            </button>
          </div>

          {/* Disagreement details — only visible when HM explicitly disagrees */}
          {hmAgreesWithAi === false && (
            <div className="space-y-3 bg-orange-50/40 border border-orange-200 rounded-xl p-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  What was wrong?
                </label>
                <div className="grid grid-cols-1 gap-1.5">
                  {DISAGREEMENT_TYPES.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => onDisagreementTypeChange(opt.value)}
                      className={`text-left py-2 px-3 rounded-lg border text-xs font-medium transition-all ${
                        hmDisagreementType === opt.value
                          ? 'border-orange-500 bg-orange-500 text-white'
                          : 'border-orange-200 bg-white text-gray-700 hover:bg-orange-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  What should have happened?
                </label>
                <textarea
                  value={hmWhatShouldHaveHappened}
                  onChange={e => onWhatShouldHaveHappenedChange(e.target.value)}
                  placeholder="e.g. Alex should have asked about distributed system tradeoffs, or the candidate's answer about the rate limiter was actually correct because..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  This feedback helps Alex learn what matters for your specific role + company.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Feedback message */}
        {message.text && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border-2 ${
            message.type === 'success'
              ? 'bg-green-50 border-green-300 text-green-700'
              : 'bg-red-50 border-red-300 text-red-700'
          }`}>
            <span>{message.type === 'success' ? '✅' : '⚠️'}</span>
            {message.text}
          </div>
        )}

        {/* Save button */}
        <button
          onClick={onSave}
          disabled={isSaving || !hmDecision}
          className="w-full py-3 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              Saving...
            </span>
          ) : '💾 Save Decision'}
        </button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// RECORDING PLAYER  (HM only)
// ═══════════════════════════════════════════════════════════════

export const MERGE_POLL_MS = 20000;
export const MERGE_POLL_TRIES = 30;

// Fallback player: the camera video starts video_offset_ms after the call audio (Deploy F measured it;
// interview 61: 1004 ms) — so audio time = video time + offset (never before the audio starts)
export function audioTimeFor(videoTime, offsetMs) {
  return Math.max(0, (Number(videoTime) || 0) + (Number(offsetMs) || 0) / 1000);
}

export function RecordingPlayer({ videoUrl, audioUrl, mergedUrl, mergePending = false, videoOffsetMs = 0, isLoading, error, onRetry, deleteAfter, videoMissingReason }) {
  const videoRef = React.useRef(null);
  const audioRef = React.useRef(null);
  const syncingRef = React.useRef(false); // prevent event loop between sync handlers
  const [audioBlocked, setAudioBlocked] = React.useState(false);

  const retentionLabel = deleteAfter
    ? `Recording will be deleted on ${new Date(deleteAfter).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
    : null;

  const hasVideo = !!videoUrl;
  const hasAudio = !!audioUrl;

  // ── Sync audio to video when both exist ──
  React.useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video || !audio || !hasVideo || !hasAudio) return;

    const syncAudio = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      const target = audioTimeFor(video.currentTime, videoOffsetMs);
      if (Math.abs(audio.currentTime - target) > 0.3) {
        audio.currentTime = target;
      }
      syncingRef.current = false;
    };
    // A blocked play() used to fail silently ("no sound") — now the visible audio bar + a note take over
    const onPlay  = () => { syncAudio(); audio.play().then(() => setAudioBlocked(false)).catch(() => setAudioBlocked(true)); };
    const onPause = () => { audio.pause(); };
    const onSeeked = () => { syncAudio(); };
    const onEnded = () => { audio.pause(); };

    video.addEventListener('play',   onPlay);
    video.addEventListener('pause',  onPause);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('ended',  onEnded);
    return () => {
      video.removeEventListener('play',   onPlay);
      video.removeEventListener('pause',  onPause);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('ended',  onEnded);
    };
  }, [hasVideo, hasAudio, videoOffsetMs]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent mx-auto mb-3"></div>
        <p className="text-gray-500 text-sm">Loading recording...</p>
      </div>
    );
  }

  // Why the video is missing (shown wherever the player has no video)
  const videoMissingNote = videoMissingReason && !hasVideo ? (
    <p className="text-amber-600 text-sm" data-testid="video-missing-reason">⚠️ {videoMissingReason}</p>
  ) : null;

  if (error && !hasVideo && !hasAudio) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
        <div className="text-5xl mb-3">🎬</div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Recording Not Available</h3>
        <p className="text-gray-500 text-sm mb-4">{error}</p>
        {videoMissingNote && <div className="mb-4">{videoMissingNote}</div>}
        <button onClick={onRetry}
          className="px-5 py-2.5 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 transition-all text-sm">
          🔄 Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xl">🎬</span>
          <div>
            <h3 className="text-white font-bold text-sm">Interview Recording</h3>
            {retentionLabel && (
              <p className="text-gray-400 text-xs mt-0.5">⏳ {retentionLabel}</p>
            )}
          </div>
        </div>
        {(mergedUrl || (hasVideo && hasAudio)) && (
          <span className="text-xs text-gray-400 bg-gray-700 px-3 py-1 rounded-full">
            {mergedUrl ? '🎙️ Video and interview audio merged' : '🎙️ Audio synced with video'}
          </span>
        )}
      </div>

      {/* Player */}
      <div className="bg-black">

        {/* Deploy F: one merged file (camera video + interview audio, already in sync) — preferred */}
        {mergedUrl && (
          <video
            key={mergedUrl}
            controls
            controlsList="nodownload"
            className="w-full max-h-[520px] object-contain"
            style={{ background: '#000' }}
            data-testid="merged-player"
          >
            <source src={mergedUrl} />
            Your browser does not support video playback.
          </video>
        )}

        {/* Not merged yet: video (muted) + audio synced via JS */}
        {!mergedUrl && hasVideo && (
          <video
            ref={videoRef}
            key={videoUrl}
            muted={hasAudio}  // muted only when audio track exists separately
            controls
            controlsList="nodownload"
            className="w-full max-h-[520px] object-contain"
            style={{ background: '#000' }}
          >
            <source src={videoUrl} />
            Your browser does not support video playback.
          </video>
        )}

        {/* Audio synced to the video via useEffect — visible controls, so sound is never silently missing */}
        {!mergedUrl && hasVideo && hasAudio && (
          <div className="px-4 py-2 bg-gray-900" data-testid="separate-audio">
            <audio ref={audioRef} key={audioUrl} preload="auto" controls controlsList="nodownload" className="w-full">
              <source src={audioUrl} />
            </audio>
            <p className="text-xs text-gray-400 mt-1">
              {audioBlocked
                ? '🔇 Your browser blocked the interview audio — press play on the audio bar above.'
                : mergePending
                  ? 'The synced recording is being prepared — this page switches to it automatically. Until then, video and interview audio play together but separately.'
                  : 'The merged recording is not ready yet — video and interview audio play together but separately.'}
            </p>
          </div>
        )}

        {/* Audio only — no video available */}
        {!hasVideo && hasAudio && (
          <div className="px-8 py-10 flex flex-col items-center gap-4">
            <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center">
              <span className="text-4xl">🎙️</span>
            </div>
            <p className="text-white text-sm font-semibold">Audio Recording</p>
            {videoMissingNote}
            <audio
              key={audioUrl}
              controls
              controlsList="nodownload"
              className="w-full max-w-lg"
            >
              <source src={audioUrl} />
              Your browser does not support audio playback.
            </audio>
          </div>
        )}

        {/* Neither available */}
        {!hasVideo && !hasAudio && (
          <div className="px-6 py-10 text-center">
            <p className="text-gray-400 text-sm">No recording available for this interview.</p>
            {videoMissingNote && <div className="mt-2">{videoMissingNote}</div>}
            <button onClick={onRetry} className="mt-3 text-xs text-purple-400 hover:text-purple-300 underline">
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          🔒 Recording access is restricted to authorized users. SAS link expires after 4 hours — refresh the page to get a new link.
        </p>
      </div>
    </div>
  );
}

export default Phase3InterviewDetail;